import React from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import IosShareIcon from '@mui/icons-material/IosShare';
import { useTheme } from "@mui/material/styles";
import { getTextColor } from '../../utils/colorUtils';
import TrimmedTextField from '../../../../components/TrimmedTextField';

/**
 * Header component for the calendar with actions
 * 
 * @param {Object} props
 * @param {string} props.mode - Mode (edit, create, or preset)
 * @param {boolean} props.isMoveMode - Whether in move mode
 * @param {string} props.deliveryGroupName - Name for new delivery groups
 * @param {Function} props.setDeliveryGroupName - Set delivery group name
 * @param {boolean} props.unsavedChanges - Whether there are unsaved changes
 * @param {boolean} props.isUpdating - Whether update is in progress
 * @param {boolean} props.isCreating - Whether creation is in progress
 * @param {boolean} props.isAdmin - Whether user is admin
 * @param {number} props.calendarItemsCount - Number of calendar items
 * @param {boolean} props.attemptedSave - Whether save was attempted
 * @param {Function} props.onSave - Handler for saving
 * @param {Function} props.onExportToPreset - Handler for exporting to preset
 * @param {Function} props.onCancelMove - Handler for canceling move
 * @param {Function} props.onClose - Handler for closing
 * @param {string} props.existingName - Existing name (for edit mode)
 * @returns {JSX.Element}
 */

const CalendarHeader = ({
  mode,
  isMoveMode,
  deliveryGroupName,
  setDeliveryGroupName,
  unsavedChanges,
  isUpdating,
  isCreating,
  isAdmin,
  calendarItemsCount,
  attemptedSave,
  onSave,
  onExportToPreset,
  onCancelMove,
  onClose,
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isMoveMode ? (
            <Button
              variant="contained"
              color="error"
              startIcon={<CancelIcon />}
              onClick={onCancelMove}
              sx={{ mr: 1 }}
            >
              Cancel Move
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={onSave}
              disabled={
                mode === 'create'
                  ? !deliveryGroupName || calendarItemsCount === 0 || isCreating
                  : !unsavedChanges || isUpdating
              }
              sx={{ mr: 1 }}
            >
              {isUpdating || isCreating ?
                <CircularProgress size={24} sx={{ color: getTextColor(theme.palette.primary.main) }} /> :
                ((mode === 'create' || mode === 'preset') ? 'Create & Apply' : 'Save Changes')}
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<IosShareIcon />}
              onClick={onExportToPreset}
              disabled={calendarItemsCount === 0 || isUpdating || isCreating || isMoveMode}
              sx={{ mr: 1 }}
            >
              Export to Preset
            </Button>
          )}
          <Button
            variant="contained"
            color="warning"
            onClick={() => onClose(false)}
            disabled={isMoveMode || isUpdating || isCreating}
          >
            Cancel
          </Button>
        </Box>
        <Box sx={{ textAlign: 'start' }}>
          {mode === 'create' || mode === 'preset' ? (
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                maxWidth: '200px'
              }}>
              </Box>
              <TrimmedTextField
                label="Journey name"
                value={deliveryGroupName}
                onChange={(e) => setDeliveryGroupName(e.target.value)}
                required
                error={attemptedSave && !deliveryGroupName.trim()}
                helperText={attemptedSave && !deliveryGroupName.trim() ? "Name is required" : ""}
                sx={{ width: '300px' }}
              />
            </Box>
          ) : (
            <Typography variant="h4">
              {deliveryGroupName || "Content Delivery"}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(CalendarHeader);